import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Dropdown, Modal, Input, Tooltip } from "antd";
import { Copy, FolderPlus, PencilSimple, Trash, ClipboardText, ShareNetwork } from "@phosphor-icons/react";
import { useDispatch, useSelector } from "react-redux";
import {
  applyFolderRename,
  createFolders,
  renameFolders,
  deleteFolder as deleteFolderThunk,
  fetchMatterDetail,
  fetchMatterDetailList,
} from "../../slice/MatterDetailsSlice";
import {
  copyDocumentsToClipboard,
  selectClipboardItems,
} from "../../slice/DocumentClipboardSlice";
import { copyDocumentsApi } from "../../service/matter";
import { addNotification } from "../../slice/SnackbarSlice";
import CustomModal from "../common/CustomModal/CustomModal";
import { getNextUntitledFolderName } from "../../utils/nextAvailableName";
import { getFolderIdentifier, resolveMatterId } from "../../utils/matterIdentity";
import {
  collectDocumentsFromTreeNode,
  copySuccessMessage,
  extractCopyApiError,
  formatPasteNote,
  pasteMenuLabel,
} from "../../utils/documentClipboard";
import { getFolderIdFromNode, selectCurrentMatterId } from "./documentMoveUtils";
import ShareFolderModal from "../ExternalShare/ShareFolderModal";

const ICON_SIZE = 16;
// Canonical danger fg from src/theme/tokens.js (status.failed.fg). Matches
// the existing CustomModal `delete` variant background palette family.
const DANGER_FG = "#A61412";

const stripFolderKey = (key) => {
  if (key === undefined || key === null || key === "") return null;
  const value = String(key);
  return value.startsWith("F") ? value.slice(1) : value;
};

const extractApiErrorMessage = (err, fallback) =>
  extractCopyApiError(err, fallback);

const dispatchToast = (dispatch, message, success = false) => {
  dispatch(
    addNotification({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      newMsg: message,
      success,
    })
  );
};

const unwrapThunkResult = (action, dispatched) => {
  if (typeof dispatched?.unwrap === "function") return dispatched.unwrap();
  if (typeof action?.unwrap === "function") return action.unwrap();
  return dispatched !== undefined
    ? Promise.resolve(dispatched)
    : Promise.reject(new Error("Action dispatch failed"));
};

/**
 * FolderContextMenu
 *
 * Wraps a folder row's title in an antd Dropdown that opens on right-click,
 * exposing New subfolder / Rename / Delete actions for that folder.
 *
 * The host row's onContextMenu calls preventDefault() so the native browser
 * menu does not pop behind antd's. Click-outside-to-close, cursor positioning,
 * and Esc-to-dismiss are handled by antd's Dropdown.
 *
 * Wires into existing Redux thunks (createFolders / renameFolders /
 * deleteFolder) and refreshes the matter tree + document list on success.
 * Does not introduce new API surface.
 */
const FolderContextMenu = ({
  folderNode,
  disabled = false,
  disabledReason = "",
  children,
}) => {
  const dispatch = useDispatch();
  const clipboardItems = useSelector(selectClipboardItems);
  const matterId = useSelector(selectCurrentMatterId);
  const matterDetails = useSelector((state) => state.matterDetails.matterDetails);
  const sortBy = useSelector((state) => state.matterDetails.sortBy);
  const sort = useSelector((state) => state.matterDetails.sort);
  const currentPage = useSelector((state) => state.matterDetails.currentPage);

  const [isNewSubfolderOpen, setIsNewSubfolderOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [pendingName, setPendingName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const parentFolderId = folderNode?.key
    ? stripFolderKey(getFolderIdentifier(folderNode) || folderNode.key)
    : null;
  const folderId = folderNode?.key ? getFolderIdFromNode(folderNode) : null;
  // Folder create still needs the Salesforce matter id. Copy/paste/refresh
  // must use the matter tree id — while a folder is open, matterDetails.id
  // is the folder, not the matter.
  const folderApiMatterId = resolveMatterId({
    currentRecord: folderNode,
    matterDetails,
    location: globalThis.location,
    treeData: folderNode ? [folderNode] : [],
  });
  const folderName =
    folderNode?.record?.name ||
    (typeof folderNode?.title === "string" ? folderNode.title : "") ||
    "";

  const childNames = useMemo(() => {
    const childNodes = Array.isArray(folderNode?.children)
      ? folderNode.children
      : [];
    return childNodes
      .map(
        (c) =>
          c?.record?.name ||
          (typeof c?.title === "string" ? c.title : null)
      )
      .filter(Boolean);
  }, [folderNode]);

  const refreshTreeAndList = useCallback(() => {
    if (!matterId) return;
    dispatch(fetchMatterDetail(matterId));
    dispatch(
      fetchMatterDetailList({
        id: matterId,
        page: currentPage || 1,
        sort_direction: sort,
        sort_by: sortBy,
      })
    );
  }, [dispatch, matterId, currentPage, sort, sortBy]);

  const closeNewSubfolder = useCallback(() => {
    if (submitting) return;
    setIsNewSubfolderOpen(false);
    setPendingName("");
  }, [submitting]);

  const closeRename = useCallback(() => {
    if (submitting) return;
    setIsRenameOpen(false);
    setPendingName("");
  }, [submitting]);

  const closeDelete = useCallback(() => {
    if (submitting) return;
    setIsDeleteOpen(false);
  }, [submitting]);

  const openNewSubfolder = useCallback(() => {
    if (disabled) return;
    setPendingName(getNextUntitledFolderName(childNames));
    setIsNewSubfolderOpen(true);
  }, [disabled, childNames]);

  const openRename = useCallback(() => {
    if (disabled) return;
    setPendingName(folderName);
    setIsRenameOpen(true);
  }, [disabled, folderName]);

  const openDelete = useCallback(() => {
    if (disabled) return;
    setIsDeleteOpen(true);
  }, [disabled]);

  const openShare = useCallback(() => {
    if (disabled || !folderId) return;
    setIsShareOpen(true);
  }, [disabled, folderId]);

  const handleNewSubfolderSubmit = useCallback(() => {
    const name = (pendingName || "").trim();
    if (!name || submitting || !parentFolderId) return;
    const createMatterId = folderApiMatterId || matterId;
    if (!createMatterId) {
      dispatchToast(dispatch, "Matter could not be resolved for this folder.", false);
      return;
    }
    setSubmitting(true);
    const action = createFolders({
      name,
      matter_id: createMatterId,
      parent_id: parentFolderId,
    });
    const dispatched = dispatch(action);
    const result = unwrapThunkResult(action, dispatched);
    result
      .then(() => {
        dispatchToast(dispatch, "Folder created.", true);
        setIsNewSubfolderOpen(false);
        setPendingName("");
        refreshTreeAndList();
      })
      .catch((err) => {
        dispatchToast(
          dispatch,
          extractApiErrorMessage(err, "Folder creation failed"),
          false
        );
      })
      .finally(() => setSubmitting(false));
  }, [
    pendingName,
    submitting,
    folderApiMatterId,
    matterId,
    parentFolderId,
    dispatch,
    refreshTreeAndList,
  ]);

  const handleRenameSubmit = useCallback(() => {
    const name = (pendingName || "").trim();
    if (!name || submitting || !folderId) return;
    if (name === folderName) {
      setIsRenameOpen(false);
      return;
    }

    // Folder renames are visually optimistic: update the loaded tree/list
    // before waiting for the network so the interaction feels immediate.
    // The original name is captured by this invocation and restored if the
    // API rejects (duplicate, validation, or network failure).
    dispatch(applyFolderRename({ folderId, name }));
    setIsRenameOpen(false);
    setPendingName("");
    setSubmitting(true);
    const action = renameFolders({ id: folderId, name });
    const dispatched = dispatch(action);
    const result = unwrapThunkResult(action, dispatched);
    result
      .then((response) => {
        dispatch(
          applyFolderRename({
            folderId,
            name,
            timeUpdated: response?.data?.time_updated,
          })
        );
        dispatchToast(dispatch, "Folder renamed.", true);
      })
      .catch((err) => {
        dispatch(applyFolderRename({ folderId, name: folderName }));
        setPendingName(name);
        setIsRenameOpen(true);
        dispatchToast(
          dispatch,
          extractApiErrorMessage(err, "Folder rename failed"),
          false
        );
      })
      .finally(() => setSubmitting(false));
  }, [pendingName, submitting, folderId, folderName, dispatch]);

  const handleCopyFolder = useCallback(() => {
    const items = collectDocumentsFromTreeNode(folderNode);
    if (!items.length) {
      dispatchToast(dispatch, "This folder has no files to copy.", false);
      return;
    }
    dispatch(copyDocumentsToClipboard({ items, sourceMatterId: matterId }));
    dispatchToast(dispatch, copySuccessMessage(items), true);
  }, [dispatch, folderNode, matterId]);

  const handlePaste = useCallback(() => {
    if (submitting || clipboardItems.length === 0) return;
    if (!folderId) {
      dispatchToast(dispatch, "This folder could not be resolved for paste.", false);
      return;
    }
    if (!matterId) {
      dispatchToast(dispatch, "Matter could not be resolved for this folder.", false);
      return;
    }
    setSubmitting(true);
    copyDocumentsApi({
      documentIds: clipboardItems.map((i) => i.id),
      targetMatterId: matterId,
      targetFolderId: folderId,
    })
      .then((response) => {
        const payload = response?.data || {};
        dispatchToast(
          dispatch,
          formatPasteNote({
            copied: payload.copied || 0,
            failed: payload.failed || 0,
            results: payload.results || [],
            destinationName: folderName,
          }),
          (payload.failed || 0) === 0
        );
        refreshTreeAndList();
      })
      .catch((err) => {
        dispatchToast(
          dispatch,
          extractApiErrorMessage(err, "Paste failed"),
          false
        );
      })
      .finally(() => setSubmitting(false));
  }, [
    submitting,
    folderId,
    folderName,
    clipboardItems,
    matterId,
    dispatch,
    refreshTreeAndList,
  ]);

  const handleDeleteConfirm = useCallback(() => {
    if (submitting || !folderId) return;
    setSubmitting(true);
    const action = deleteFolderThunk({ folderId });
    const dispatched = dispatch(action);
    const result = unwrapThunkResult(action, dispatched);
    result
      .then(() => {
        dispatchToast(dispatch, "Folder moved to Recycle Bin.", true);
        setIsDeleteOpen(false);
        refreshTreeAndList();
      })
      .catch((err) => {
        dispatchToast(
          dispatch,
          extractApiErrorMessage(err, "Folder delete failed"),
          false
        );
      })
      .finally(() => setSubmitting(false));
  }, [submitting, folderId, dispatch, refreshTreeAndList]);

  // Wrap a label in a tooltip when the menu is disabled, so the user sees
  // the reason on hover. antd Menu items don't surface tooltips natively.
  const labelWithDisabledTooltip = (label) => {
    if (!disabled || !disabledReason) return label;
    return (
      <Tooltip title={disabledReason} placement="right">
        <span>{label}</span>
      </Tooltip>
    );
  };

  const items = useMemo(
    () => [
      {
        key: "copy-folder",
        label: "Copy files in this folder",
        icon: <Copy size={ICON_SIZE} weight="regular" />,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          handleCopyFolder();
        },
      },
      {
        key: "paste-documents",
        label: pasteMenuLabel(clipboardItems.length),
        icon: <ClipboardText size={ICON_SIZE} weight="regular" />,
        disabled: submitting || clipboardItems.length === 0,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          handlePaste();
        },
      },
      { key: "divider-copy", type: "divider" },
      {
        key: "new-subfolder",
        label: labelWithDisabledTooltip("New subfolder"),
        icon: <FolderPlus size={ICON_SIZE} weight="regular" />,
        disabled,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          openNewSubfolder();
        },
      },
      {
        key: "rename",
        label: labelWithDisabledTooltip("Rename"),
        icon: <PencilSimple size={ICON_SIZE} weight="regular" />,
        disabled,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          openRename();
        },
      },
      {
        key: "share-folder",
        label: labelWithDisabledTooltip("Share folder externally"),
        icon: <ShareNetwork size={ICON_SIZE} weight="regular" />,
        disabled,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          openShare();
        },
      },
      { key: "divider-1", type: "divider" },
      {
        key: "delete",
        label: labelWithDisabledTooltip(
          <span style={{ color: disabled ? undefined : DANGER_FG }}>Delete</span>
        ),
        icon: (
          <Trash
            size={ICON_SIZE}
            weight="regular"
            color={disabled ? undefined : DANGER_FG}
          />
        ),
        danger: true,
        disabled,
        onClick: ({ domEvent }) => {
          domEvent?.stopPropagation?.();
          openDelete();
        },
      },
    ],
    // labelWithDisabledTooltip is a stable closure over `disabled`/`disabledReason`
    // which are already in the dep list; intentionally not adding it to keep
    // useMemo identity stable when only handlers change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled, disabledReason, openNewSubfolder, openRename, openDelete, openShare, clipboardItems, handlePaste, handleCopyFolder, submitting]
  );

  const handleShareSnackbar = useCallback(
    (message, success) => dispatchToast(dispatch, message, success),
    [dispatch]
  );

  // Mandatory: prevent the native browser context menu so antd's overlay is
  // the only menu visible on right-click.
  const handleHostContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      <Dropdown
        menu={{ items }}
        trigger={["contextMenu"]}
        overlayStyle={{ minWidth: 180 }}
      >
        <span
          data-testid="folder-context-menu-host"
          data-tree-context-menu-host=""
          onContextMenu={handleHostContextMenu}
          style={{ display: "inline-block", width: "100%" }}
        >
          {children}
        </span>
      </Dropdown>

      <Modal
        title="New subfolder"
        open={isNewSubfolderOpen}
        onCancel={closeNewSubfolder}
        onOk={handleNewSubfolderSubmit}
        okText={submitting ? "Creating…" : "Create"}
        cancelText="Cancel"
        okButtonProps={{
          disabled: submitting || !pendingName?.trim(),
        }}
        cancelButtonProps={{ disabled: submitting }}
        destroyOnClose
        maskClosable={!submitting}
      >
        <div style={{ marginBottom: 8 }}>
          Create a new folder inside <strong>{folderName}</strong>.
        </div>
        <Input
          autoFocus
          value={pendingName}
          onChange={(e) => setPendingName(e.target.value)}
          onPressEnter={handleNewSubfolderSubmit}
          placeholder="Folder name"
          maxLength={255}
          aria-label="New folder name"
          disabled={submitting}
        />
      </Modal>

      <Modal
        title="Rename folder"
        open={isRenameOpen}
        onCancel={closeRename}
        onOk={handleRenameSubmit}
        okText={submitting ? "Renaming…" : "Rename"}
        cancelText="Cancel"
        okButtonProps={{
          disabled: submitting || !pendingName?.trim(),
        }}
        cancelButtonProps={{ disabled: submitting }}
        destroyOnClose
        maskClosable={!submitting}
      >
        <div style={{ marginBottom: 8 }}>
          Rename <strong>{folderName}</strong>.
        </div>
        <Input
          autoFocus
          value={pendingName}
          onChange={(e) => setPendingName(e.target.value)}
          onPressEnter={handleRenameSubmit}
          placeholder="Folder name"
          maxLength={255}
          aria-label="Folder name"
          disabled={submitting}
        />
      </Modal>

      <CustomModal
        open={isDeleteOpen}
        onClose={closeDelete}
        heading={`Delete '${folderName}'?`}
        content="The folder will be moved to the Recycle Bin and can be restored later."
        btnTxtPrimary={submitting ? "Deleting…" : "Delete"}
        btnTxtSecondary="Cancel"
        onPrimaryClick={handleDeleteConfirm}
        onSecondaryClick={closeDelete}
        disabled={submitting}
        variant="delete"
      />

      {isShareOpen && (
        <ShareFolderModal
          isOpen={isShareOpen}
          onClose={() => setIsShareOpen(false)}
          folder_id={folderId}
          handleSnackbar={handleShareSnackbar}
        />
      )}
    </>
  );
};

FolderContextMenu.propTypes = {
  folderNode: PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.any,
    record: PropTypes.object,
    type: PropTypes.string,
    children: PropTypes.array,
  }).isRequired,
  disabled: PropTypes.bool,
  disabledReason: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default FolderContextMenu;
